import Section from "@bmi-digital/components/section";
import { graphql } from "gatsby";
import React, { useEffect } from "react";
import type { PayloadEmbeddedScriptSection } from "../types/payload";

export type EmbeddedScriptSectionData = Pick<
  PayloadEmbeddedScriptSection,
  "blockType" | "title" | "scriptId" | "url" | "ecmaScript"
>;

type Props = {
  data: EmbeddedScriptSectionData;
  "data-testid"?: string;
};

const EmbeddedScriptSection = ({
  data: { title, scriptId, url, ecmaScript },
  "data-testid": testId
}: Props) => {
  useEffect(() => {
    if (!scriptId.trim() || !url.trim()) {
      return;
    }

    const cacheBustedUrl = new URL(url);
    cacheBustedUrl.searchParams.set("_t", Date.now().toString());

    const script = document.createElement("script");

    script.src = cacheBustedUrl.toString();
    script.async = true;
    script.setAttribute("data-testid", "embedded-script");
    const { pathname } = cacheBustedUrl;
    if (
      (pathname.endsWith(".js") && ecmaScript === "true") ||
      pathname.endsWith(".mjs")
    ) {
      script.type = "module";
    }
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [ecmaScript, scriptId, url]);

  if (!scriptId.trim() || !url.trim()) {
    return;
  }

  return (
    <Section>
      {title && <Section.Title>{title}</Section.Title>}
      <div
        id={scriptId}
        data-testid={testId ?? "embedded-script-section"}
      ></div>
    </Section>
  );
};

export default EmbeddedScriptSection;

export const query = graphql`
  fragment EmbeddedScriptSectionFragment on PayloadEmbeddedScriptSection {
    blockType
    title
    scriptId
    url
    ecmaScript
  }
`;
